import Box from '@mui/material/Box';
import {
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Stack, useTheme } from '@mui/system';

//Cost in dollars per team, per month
const INSIGHT_PRO_PRICE = '$1,250';

const subHeadingText =
    'For teams looking to gain deeper insight into the drivers of decision-making skill';

const featureListItems = [
    'Break down Behavioral Alpha® Score into underlying decision types',
    'Unlock peer quartile ranking and monitor it over time',
    'Track consistency of Behavioral Alpha® Score through market environments',
    'Includes access for up to 5 users within a team'
];

const TypographyWhite = styled(Typography)(({ theme }) => ({
    color: theme.palette.common.white
}));

/**
 * Custom SVG checkbox icon with proportional dimensions relative to size prop
 * @component
 * @param {Number} size - icon size in px
 * @param {String} tickColor - color of tick
 * @returns {React.ReactElement}
 */
const CheckBoxIcon = ({ size, tickColor }) => (
    <svg width={size} height={size} fill='none'>
        <rect width='100%' height='100%' rx='50%' fill='white' />
        <CheckRoundedIcon
            x='20%'
            width='62%'
            display='none'
            sx={{ color: tickColor }}
        />
    </svg>
);

CheckBoxIcon.propTypes = {
    size: PropTypes.number.isRequired,
    tickColor: PropTypes.string
};

/**
 * Renders pricing information for Insight Pro, including a monthly price and features list.
 * @component
 * @returns {React.ReactElement}
 */
export const PricingShowcase = () => {
    const theme = useTheme();

    return (
        <Box>
            <Stack direction='row'>
                <TypographyWhite sx={{ fontWeight: 700, fontSize: '1.55rem' }}>
                    Insight Pro
                </TypographyWhite>
                <Box
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    sx={{
                        backgroundColor: theme.palette.orange.main,
                        borderRadius: '20px',
                        height: '18px',
                        width: '38px'
                    }}
                >
                    <TypographyWhite
                        sx={{
                            fontSize: 10,
                            fontWeight: 500
                        }}
                    >
                        New
                    </TypographyWhite>
                </Box>
            </Stack>
            <TypographyWhite
                paragraph
                sx={{ fontSize: '0.82rem', lineHeight: '1rem' }}
            >
                {subHeadingText}
            </TypographyWhite>

            <TypographyWhite
                sx={{
                    lineHeight: '1.7rem',
                    fontWeight: 700,
                    fontSize: '1.55rem'
                }}
            >
                {INSIGHT_PRO_PRICE}
                <TypographyWhite
                    component='span'
                    sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        paddingLeft: 0.3
                    }}
                >
                    per month
                </TypographyWhite>
            </TypographyWhite>
            <TypographyWhite paragraph sx={{ fontSize: '0.7rem' }}>
                Billed Annually
            </TypographyWhite>

            <TypographyWhite
                sx={{ fontWeight: 700, fontSize: '0.8rem', paddingBottom: 1 }}
            >
                Everything in Basic, plus:
            </TypographyWhite>

            <List dense disablePadding>
                {featureListItems.map((item) => (
                    <ListItem
                        sx={{ paddingLeft: 0, paddingRight: 0 }}
                        key={item}
                    >
                        <ListItemIcon sx={{ minWidth: '40px' }}>
                            <CheckBoxIcon
                                size={18}
                                tickColor={theme.palette.pricingCheckBoxTick}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                                fontSize: '0.82rem',
                                lineHeight: '0.85rem',
                                color: 'white'
                            }}
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};
